import React, { useRef, useLayoutEffect, useEffect, memo } from 'react';
import { Button, ButtonVariant, ButtonState } from '@sas/nova-commons/components/Button';
import { StackLink } from './StackLink';
import { ButtonType } from '@sas-dvr/ltjs-commons';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { PageControlDotSelected, PageControlDot } from '@sas/react-svg';
// Tab became internal in NOVACOMMONS-893
// eslint-disable-next-line no-restricted-imports
import { Tab } from '@sas/nova-commons/components/TabBar/__internal__';
// eslint-disable-next-line no-restricted-imports
import { DraggableTab } from '@sas/nova-commons/components/TabBar/__internal__/dnd/DraggableTab';
import cn from './StackButton.css.json';
import './StackButton.css';
export const dragType = 'dragType';
const tabTextStyle = {
    textOverflow: 'ellipsis',
    display: 'block',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '25rem',
    alignItems: 'flex-start',
};
export const StackButton = memo((props) => {
    const { selected, focused, isFocusActive } = props;
    const ref = useRef(null);
    const scrollTo = props.scrollTo;
    const wrapText = props.tabLocation === 'right' || props.tabLocation === 'left';
    const dt = useDesignTokens();
    useLayoutEffect(() => {
        if (selected && ref.current) {
            scrollTo(ref.current.getBoundingClientRect());
        }
    }, [selected, scrollTo]);
    /**
     * In the case of a <Tab>, this ref never gets set.
     * The Tab component will focus itself (focus prop).
     */
    const focusRef = useRef(null);
    useEffect(() => {
        var _a;
        if (focused && isFocusActive) {
            (_a = focusRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [focused, isFocusActive]);
    const onPress = () => props.onChange(props.index);
    let wrapperCn = undefined;
    const button = (() => {
        switch (props.buttonType) {
            case ButtonType.DOT: {
                return (React.createElement(Button, { ref: focusRef, icon: { icon: selected ? PageControlDotSelected : PageControlDot }, focusable: focused, variant: ButtonVariant.Light, onPress: onPress, tokens: () => ({ bg: 'transparent' }), "aria-selected": selected }));
            }
            case ButtonType.LINK:
                return (React.createElement(StackLink, { ref: focusRef, label: props.label, key: props.index, selected: selected, wrapText: wrapText, onClick: onPress, tabIndex: focused ? 0 : -1 }));
            case ButtonType.TAB:
                wrapperCn = cn.stretch;
                return (React.createElement(Tab, { className: cn.tab, textStyle: Object.assign(Object.assign({}, tabTextStyle), { whiteSpace: wrapText ? 'normal' : 'nowrap' }), label: props.label, selected: selected, focused: focused && isFocusActive, onPress: onPress, location: props.tabLocation, tokenOverrides: () => ({ bg: 'transparent' }), tabIndex: focused ? 0 : -1, "aria-selected": selected, allowDrag: true }));
            case ButtonType.BUTTON:
            default:
                wrapperCn = cn.stretch;
                return (React.createElement(Button, { ref: focusRef, text: props.label, focusable: focused, className: cn.stackButton[props.tabLocation], onPress: onPress, tokens: ({ state }) => StackButtonTokens(selected, state, dt), "aria-selected": selected }));
        }
    })();
    return (React.createElement("div", { ref: ref, className: wrapperCn },
        React.createElement(DraggableTab, { key: props.index, id: `${props.dndPrefix}-dndTab-${props.index}`, allowDrag: props.enableDrag, allowDrop: props.enableDrag, acceptDropTypes: [dragType], index: props.index, dndPrefix: props.dndPrefix, type: dragType, droppableContent: [dragType], reorderTabs: props.reorderItem, 
            // These props are optional on TabBar but required on the internal component.
            onDragLeave: () => { }, onDragOver: () => { } }, button)));
});
/**
 * The button bar in the tab area of a stack container has its own unique
 * set of styles that does *not* match the button bars elsewhere.
 */
function StackButtonTokens(selected, state, dt) {
    if (selected) {
        return dt.reportTokens.stateSecondary.selected;
    }
    if (state === ButtonState.Normal) {
        return { bg: 'transparent' };
    }
    return {};
}
